import { Opportunity, PrismTransaction, TransactionTeam } from '@repo/services';
import {
  getTransactionStatusColorByName,
  getTransactionTeamStatusShorthand,
  transactionStatus
} from '@repo/services';

/**
 * Ordered list of opportunity/transaction checklist milestones (Draft, Mapping, Pending) with display descriptions, used to translate raw milestone values into human-readable status names.
 */
export const opportunityMilestones = [
  { aimChecklistMilestone: 'Draft', type: 'opportunity', description: 'Draft', sequence: 1 },
  { aimChecklistMilestone: 'Mapping', type: 'opportunity', description: 'Mapping', sequence: 2 },
  { aimChecklistMilestone: 'Pending', type: 'opportunity', description: 'Pending Qualification', sequence: 3 },
  { aimChecklistMilestone: 'Pending', type: 'transaction', description: 'Pending Team Formation', sequence: 4 }
]
/**
 * Looks up the display-friendly opportunity status name for a given raw checklist milestone value.
 *
 * @param statusString - Raw status/milestone string to translate.
 * @returns The matching opportunity milestone's display description, or the original string if no match is found; empty string if statusString is falsy.
 */
export const getStatusName = (statusString: string | undefined) => {
  if (statusString) {
    const statusName = opportunityMilestones.find((m) => m.aimChecklistMilestone?.toLowerCase() === statusString?.toLowerCase() && m.type === 'opportunity')?.description || statusString;
    return statusName;
  }
  return '';
}

/**
 * Array of status strings that indicate a transaction has reached its historical endpoint.
 */
export const historicStatuses = ['canceled', 'historic', 'rejected', 'failed', 'declined'];
/**
 * Array of status strings that indicate a transaction has been fully completed.
 *
 * This is a simple array of strings — the values match those in historicStatuses.
 */
export const completedStatuses = ['completed', 'complete', 'postclose'];
/**
 * Gray color literal used for historic-related UI elements.
 */
export const historicGrey = 'rgb(150,150,150)';
/**
 * Checks whether a transaction's final status is "failed".
 *
 * @param transaction - The transaction to check.
 * @returns True if the transaction's finalStatus is 'failed'.
 */
export const isFailed = (transaction: PrismTransaction) =>
  transaction?.finalStatus?.toLowerCase() === 'failed';

/**
 * Checks whether a transaction's final status is "rejected".
 *
 * @param transaction - The transaction to check.
 * @returns True if the transaction's finalStatus is 'rejected'.
 */
export const isRejected = (transaction: PrismTransaction) => {
  return transaction?.finalStatus?.toLowerCase() === 'rejected';
}

/**
 * Determines whether a PrismTransaction is historic (canceled, historic, rejected, failed, or declined).
 *
 * @param transaction - The transaction to check.
 * @returns True if the transaction is canceled or its status/checklist milestone matches one of the historic statuses.
 */
export const isHistoric = (transaction: PrismTransaction) =>
  transaction?.isCanceled
  || historicStatuses?.includes(transaction?.status?.toLowerCase())
  || historicStatuses?.includes(transaction?.checklistMilestone?.value?.toLowerCase())

/**
 * Determines whether a TransactionTeam record is historic (canceled, historic, rejected, failed, or declined).
 *
 * @param transaction - The transaction team record to check.
 * @returns True if the transaction team is canceled or its teamStatus/aimChecklistMilestone matches one of the historic statuses.
 */
export const isHistoricTxTeam = (transaction: TransactionTeam) =>
  transaction?.isCanceled
  || historicStatuses?.includes(transaction?.teamStatus?.toLowerCase())
  || historicStatuses?.includes(transaction?.aimChecklistMilestone?.toLowerCase())

/**
 * Determines whether a PrismTransaction is considered completed — not canceled and whose status, investment phase, or checklist milestone matches one of the completed statuses.
 *
 * @param transaction - The transaction to check.
 * @returns `true` if not canceled and any of its status fields match a completed status.
 */
export const isCompleted = (transaction: PrismTransaction) =>
  !transaction.isCanceled &&
  (completedStatuses.includes(transaction.status?.toLowerCase()) ||
    completedStatuses.includes(transaction.investmentPhase?.value?.toLowerCase()) ||
    completedStatuses.includes(transaction.checklistMilestone?.value?.toLowerCase()));

/**
 * Determines whether a TransactionTeam is considered completed — not canceled and whose team status or checklist milestone matches one of the completed statuses.
 *
 * @param transaction - The transaction team to check.
 * @returns `true` if not canceled and either its team status or checklist milestone matches a completed status.
 */
export const isCompletedTxTeam = (transaction: TransactionTeam) =>
  !transaction.isCanceled &&
  (completedStatuses.includes(transaction.teamStatus?.toLowerCase()) ||
    completedStatuses.includes(transaction.aimChecklistMilestone?.toLowerCase()));

/**
 * Determines whether a PrismTransaction is currently active (not canceled and not completed).
 *
 * @param transaction - The transaction to check.
 * @returns True if the transaction is not canceled and not in a completed status.
 */
export const isActive = (transaction: PrismTransaction) =>
  !transaction.isCanceled &&
  !completedStatuses.includes(transaction.status?.toLowerCase()) &&
  !completedStatuses.includes(transaction?.checklistMilestone?.value?.toLowerCase());

/**
 * Determines whether a TransactionTeam record is currently active (not canceled and not completed).
 *
 * @param transaction - The transaction team record to check.
 * @returns True if the transaction team is not canceled and not in a completed status.
 */
export const isActiveTxTeam = (transaction: TransactionTeam) =>
  !transaction.isCanceled &&
  !completedStatuses.includes(transaction.teamStatus?.toLowerCase()) &&
  !completedStatuses.includes(transaction?.aimChecklistMilestone?.toLowerCase());

/**
 * Filters a list of transactions down to those that are historic.
 *
 * @param transactions - Transactions to filter.
 * @returns Only the transactions considered historic.
 */
export const filterHistoricTransactions = (transactions: PrismTransaction[]) => {
  return transactions.filter(isHistoric);
};

/**
 * Filters a list of transactions down to those that failed.
 *
 * @param transactions - Transactions to filter.
 * @returns Only the transactions with a finalStatus of 'failed'.
 */
export const filterFailedTransactions = (transactions: PrismTransaction[]) => {
  return transactions.filter(isFailed);
};

/**
 * Filters a list of transactions down to those that were rejected.
 *
 * @param transactions - Transactions to filter.
 * @returns Only the transactions with a finalStatus of 'rejected'.
 */
export const filterRejectedTransactions = (transactions: PrismTransaction[]) => {
  return transactions.filter(isRejected);
};

/**
 * Filters a list of transactions down to those that are completed.
 *
 * @param transactions - Transactions to filter.
 * @returns Only the transactions considered completed.
 */
export const filterCompletedTransactions = (transactions: PrismTransaction[]) => {
  return transactions.filter(isCompleted);
};

/**
 * Filters a list of transactions down to those that are active.
 *
 * @param transactions - Transactions to filter.
 * @returns Only the transactions considered active.
 */
export const filterActiveTransactions = (transactions: PrismTransaction[]) => {
  return transactions.filter(isActive);
};

/**
 * Filters a list of transactions down to dispositions only.
 *
 * @param transactions - Transactions to filter.
 * @returns Only the transactions whose transaction type is a disposition.
 */
export const filterDispositions = (transactions: PrismTransaction[]) => {
  return transactions.filter((tx) => tx?.transactionType?.aimTransactionType?.toLowerCase() === 'disposition');
}

/**
 * Removes disposition transactions from a list, keeping all other transaction types.
 *
 * @param transactions - Transactions to filter.
 * @returns Only the transactions whose transaction type is not a disposition.
 */
export const scrubDispositions = (transactions: PrismTransaction[]) => {
  return transactions.filter((tx) => tx?.transactionType?.aimTransactionType?.toLowerCase() !== 'disposition');
}

/**
 * Returns a computed status string for a transaction by checking historic, completed, and active states in order, falling back to 'pending'.
 *
 * @param transaction - The transaction to evaluate.
 * @returns One of 'historic', 'completed', 'active', or 'pending'.
 *
 * @example
 * getStatusString(historicTx); // 'historic'
 * getStatusString(canceledTx); // 'historic'
 * getStatusString(activeTx); // 'active'
 */
export const getStatusString = (transaction: PrismTransaction) => {
  if (isHistoric(transaction)) return 'historic';
  else if (isCompleted(transaction)) return 'completed';
  else if (isActive(transaction)) return 'active';
  else return 'pending';
};

/**
 * Returns a computed status string for a TransactionTeam by checking historic, completed, and active states in order, falling back to 'pending'.
 *
 * @param transaction - The transaction team to evaluate.
 * @returns One of 'historic', 'completed', 'active', or 'pending'.
 */
export const getStatusStringTxTeam = (transaction: TransactionTeam) => {
  if (isHistoricTxTeam(transaction)) return 'historic';
  else if (isCompletedTxTeam(transaction)) return 'completed';
  else if (isActiveTxTeam(transaction)) return 'active';
  else return 'pending';
};

// export const getOppOrTxStatusString = (transaction?: TransactionTeam | ) => {
//   if (!transaction) return '';
//     if (isHistoric(transaction)) return transaction?.finalStatus;
//     if (!transaction?.checklistMilestone?.value) return transaction?.status;
//     if (transaction?.checklistMilestone?.value) return transaction?.checklistMilestone?.value;
//     return '';
// };

/**
 * Returns the status string for either an Opportunity or a TransactionTeam, checking for historic/completed states from TransactionTeam when applicable.
 *
 * @param transaction - An Opportunity or TransactionTeam (or `undefined`).
 * @returns "The status string, or `''` if the value is falsy."
 *
 * @example
 * getOppOrTxStatusString({ aimOpportunity: true, status: 'qualified' }); // 'qualified'
 * getOppOrTxStatusString({ finalStatus: 'rejected' }); // 'rejected'
 * getOppOrTxStatusString(undefined); // ''
 */
export const getOppOrTxStatusString = (transaction: Opportunity | TransactionTeam | undefined) => {
  if (!transaction) return '';
  const isOpportunity = (transaction as Opportunity)?.aimOpportunity;
  if (isOpportunity) {
    const opp = transaction as Opportunity;
    return opp?.status;
  } else {
    const tx = transaction as TransactionTeam;
    if (isHistoricTxTeam(tx)) return tx?.finalStatus;
    if (!tx?.aimChecklistMilestone) return tx?.teamStatus;
    if (tx?.aimChecklistMilestone) return tx?.aimChecklistMilestone;
    return '';
  }
};

/**
 * Returns the color for a transaction's status by looking up the record name in the `transactionStatus` map, falling back to `getTransactionStatusColorByName`.
 *
 * @param transaction - The transaction whose status color to resolve.
 * @returns "The color string from the matching status, or the result of `getTransactionStatusColorByName` for the fallback record."
 */
export const getTransactionStatusColor = (transaction: PrismTransaction) => {
  const record = transaction.isCanceled
    ? transaction.finalStatus || transaction.status
    : transaction?.checklistMilestone?.value || transaction.status;
  if (Object.keys(transactionStatus).includes(record)) {
    return transactionStatus[record as keyof typeof transactionStatus]?.color;
  }
  return getTransactionStatusColorByName(record);
};

/**
   * Resolves a shorthand status string for a PrismTransaction by extracting
   * the underlying record name and passing it to `getTransactionTeamStatusShorthand`.
   *
   * @param transaction - The transaction to look up.
   * @returns The shorthand status string from `getTransactionTeamStatusShorthand`.
   */
  export const getTransactionTeamStatusShorthandTx = (transaction: PrismTransaction) => {
  const record = transaction.isCanceled
    ? transaction.finalStatus || transaction.status
    : transaction?.checklistMilestone?.value || transaction.status;
  return getTransactionTeamStatusShorthand(record);
};

// **** FOR WES DASHBOARD CHANGES **** //

/** Milestone values that classify a transaction as being in the Pipeline phase. */
export const pipelineMilestones = ['FORMED', 'PRELIMANALYSIS']

/** Milestone values that classify a transaction as being in the EIT-Approved phase. */
export const eitMilestones = ['APPROVALS', 'NEGOTIATING']

/** Milestone values that classify a transaction as being in the Under-Contract phase. */
export const underContractMilestones = ['DUEDILIGENCE', 'CLOSING']

/**
* Filters transactions to only those in the active Pipeline phase.
*
* @param transactions - Full list of prism transactions.
* @param includeDispositions - When false (default), disposal transactions are excluded.
* @returns Active transactions whose `investmentPhase` is "pipeline".
*/
/**
* Returns active transactions currently in the Pipeline investment phase.
*
* @param transactions - All transactions to filter.
* @param includeDispositions - Whether to keep disposed transactions; defaults to false.
* @returns Active pipeline transactions, optionally excluding dispositions.
*/
export const filterPipelineTransactions = (transactions: PrismTransaction[], includeDispositions = false) => {
  let activeTxs = filterActiveTransactions(transactions);
  if (!includeDispositions) {
    activeTxs = scrubDispositions(activeTxs);
  }
  return activeTxs.filter((tx) => tx?.investmentPhase?.value?.toLowerCase() === 'pipeline');
}

/**
* Returns only the disposition (disposal) transactions from the Pipeline phase.
*
* @param transactions - Full list of prism transactions.
* @returns Pipeline transactions that are dispositions.
*/
/**
* Returns disposition records among active Pipeline transactions.
*
* @param transactions - All transactions to filter.
* @returns Pipeline transactions that are dispositions.
*/
export const filterPipelineDispositions = (transactions: PrismTransaction[]) => {
  const txs = filterPipelineTransactions(transactions, true);
  return filterDispositions(txs);
}

/**
* Filters transactions to only those in the historic Pipeline phase.
*
* @param transactions - Full list of prism transactions.
* @param includeDispositions - When false (default), disposal transactions are excluded.
* @returns Historic transactions whose `investmentPhase` is "pipeline".
*/
/**
* Returns historic (closed) transactions that were in the Pipeline investment phase.
*
* @param transactions - All transactions to filter.
* @param includeDispositions - Whether to keep disposed transactions; defaults to false.
* @returns Historic pipeline transactions, optionally excluding dispositions.
*/
export const filterHistoricPipelineTransactions = (transactions: PrismTransaction[], includeDispositions = false) => {
  let historicTxs = filterHistoricTransactions(transactions);
  if (!includeDispositions) {
    historicTxs = scrubDispositions(historicTxs);
  }
  return historicTxs.filter((tx) => tx?.investmentPhase?.value?.toLowerCase() === 'pipeline');
}

/**
* Returns only the disposition transactions from the historic Pipeline phase.
*
* @param transactions - Full list of prism transactions.
* @returns Historic Pipeline transactions that are dispositions.
*/
/**
* Returns disposition records among historic Pipeline transactions.
*
* @param transactions - All transactions to filter.
* @returns Historic pipeline transactions that are dispositions.
*/
export const filterHistoricPipelineDispositions = (transactions: PrismTransaction[]) => {
  const historicTxs = filterHistoricPipelineTransactions(transactions, true);
  return filterDispositions(historicTxs);
}

/**
* Filters transactions to only those in the active EIT-Approved phase.
*
* @param transactions - Full list of prism transactions.
* @param includeDispositions - When false (default), disposal transactions are excluded.
* @returns Active transactions whose `investmentPhase` is "eitapproved".
*/
/**
* Returns active transactions currently in the EIT-Approved investment phase.
*
* @param transactions - All transactions to filter.
* @param includeDispositions - Whether to keep disposed transactions; defaults to false.
* @returns Active EIT-approved transactions, optionally excluding dispositions.
*/
export const filterEITApprovedTransactions = (transactions: PrismTransaction[], includeDispositions = false) => {
  let activeTxs = filterActiveTransactions(transactions);
  if (!includeDispositions) {
    activeTxs = scrubDispositions(activeTxs);
  }
  return activeTxs.filter((tx) => tx?.investmentPhase?.value?.toLowerCase() === 'eitapproved');
}

/**
* Returns only the disposition transactions from the active EIT-Approved phase.
*
* @param transactions - Full list of prism transactions.
* @returns Active EIT-Approved transactions that are dispositions.
*/
/**
* Returns disposition records among active EIT-Approved transactions.
*
* @param transactions - All transactions to filter.
* @returns Active EIT-approved transactions that are dispositions.
*/
export const filterEITApprovedDispositions = (transactions: PrismTransaction[]) => {
  const txs = filterEITApprovedTransactions(transactions, true);
  return filterDispositions(txs);
}

/**
* Filters transactions to those in the historic post-EIT phases (EIT-Approved or Under-Contract).
*
* @param transactions - Full list of prism transactions.
* @param includeDispositions - When false (default), disposal transactions are excluded.
* @returns Historic transactions whose `investmentPhase` is "eitapproved" or "undercontract".
*/
/**
* Returns historic transactions that were in the post-EIT phases: EIT-Approved or Under Contract.
*
* @param transactions - All transactions to filter.
* @param includeDispositions - Whether to keep disposed transactions; defaults to false.
* @returns Historic post-EIT transactions, optionally excluding dispositions.
*/
export const filterHistoricPostEITTransactions = (transactions: PrismTransaction[], includeDispositions = false) => {
  let historicTxs = filterHistoricTransactions(transactions);
  if (!includeDispositions) {
    historicTxs = scrubDispositions(historicTxs);
  }
  return historicTxs.filter((tx) => tx?.investmentPhase?.value?.toLowerCase() === 'eitapproved' || tx?.investmentPhase?.value?.toLowerCase() === 'undercontract');
}

/**
* Returns only the disposition transactions from the historic post-EIT phases (EIT-Approved + Under-Contract).
*
* @param transactions - Full list of prism transactions.
* @returns Historic post-EIT transactions that are dispositions.
*/
/**
* Returns disposition records among historic post-EIT transactions (EIT-Approved + Under Contract).
*
* @param transactions - All transactions to filter.
* @returns Historic post-EIT transactions that are dispositions.
*/
export const filterHistoricPostEitDispositions = (transactions: PrismTransaction[]) => {
  const historicTxs = filterHistoricPostEITTransactions(transactions, true);
  return filterDispositions(historicTxs);
}

/**
* Filters transactions to only those in the active Under-Contract phase.
*
* @param transactions - Full list of prism transactions.
* @param includeDispositions - When false (default), disposal transactions are excluded.
* @returns Active transactions whose `investmentPhase` is "undercontract".
*/
/**
* Returns active transactions currently in the Under Contract investment phase.
*
* @param transactions - All transactions to filter.
* @param includeDispositions - Whether to keep disposed transactions; defaults to false.
* @returns Active under-contract transactions, optionally excluding dispositions.
*/
export const filterUnderContractTransactions = (transactions: PrismTransaction[], includeDispositions = false) => {
  let activeTxs = filterActiveTransactions(transactions);
  if (!includeDispositions) {
    activeTxs = scrubDispositions(activeTxs);
  }
  return activeTxs.filter((tx) => tx?.investmentPhase?.value?.toLowerCase() === 'undercontract');
}

/**
* Returns only the disposition transactions from the active Under-Contract phase.
*
* @param transactions - Full list of prism transactions.
* @returns Active Under-Contract transactions that are dispositions.
*/
/**
* Returns disposition records among active Under Contract transactions.
*
* @param transactions - All transactions to filter.
* @returns Active under-contract transactions that are dispositions.
*/
export const filterUnderContractDispositions = (transactions: PrismTransaction[]) => {
  const txs = filterUnderContractTransactions(transactions, true);
  return filterDispositions(txs);
}

/**
* Filters transactions to all historic (completed or discontinued) transactions regardless of phase.
*
* @param transactions - Full list of prism transactions.
* @param includeDispositions - When false (default), disposal transactions are excluded.
* @returns All historic transactions, optionally filtered to exclude dispositions.
*/
/**
* Returns all historic (closed) transactions regardless of phase.
*
* @param transactions - All transactions to filter.
* @param includeDispositions - Whether to keep disposed transactions; defaults to false.
* @returns All historic transactions, optionally excluding dispositions.
*/
export const filterAllHistoricTransactions = (transactions: PrismTransaction[], includeDispositions = false) => {
  let historicTxs = filterHistoricTransactions(transactions);
  if (!includeDispositions) {
    historicTxs = scrubDispositions(historicTxs);
  }
  return historicTxs;
}

/**
* Returns only the disposition transactions from the full set of historic transactions.
*
* @param transactions - Full list of prism transactions.
* @returns All historic transactions that are dispositions.
*/
/**
* Returns disposition records among all historic transactions across every phase.
*
* @param transactions - All transactions to filter.
* @returns Historic transactions that are dispositions.
*/
export const filterAllHistoricDispositions = (transactions: PrismTransaction[]) => {
  const historicTxs = filterAllHistoricTransactions(transactions, true);
  return filterDispositions(historicTxs);
}

/**
* Determines the display phase label for a transaction: "Historic", "Completed", "Pipeline",
* "EITApproved", "UnderContract", or an empty string if none match.
*
* @param transaction - The prism transaction to evaluate.
* @returns A string phase label suitable for UI display.
*/
/**
* Resolves the human-readable phase name for a transaction based on its status and milestone.
*
* @param transaction - The transaction to evaluate.
* @returns One of "Historic", "Completed", "Pipeline", "EITApproved", "UnderContract", or `""` if none match.
*/
export const getTransactionPhase = (transaction: PrismTransaction) => {
  if (isHistoric(transaction)) return 'Historic';
  if (isCompleted(transaction)) return 'Completed';
  if (!transaction?.investmentPhase?.value || transaction?.investmentPhase?.value?.toLowerCase() === 'pipeline') {
    return 'Pipeline';
  } else if (transaction?.investmentPhase?.value?.toLowerCase() === 'eitapproved') {
    return 'EITApproved';
  } else if (transaction?.investmentPhase?.value?.toLowerCase() === 'undercontract') {
    return 'UnderContract';
  } else {
    return '';
  }
}

// / **** FOR WES DASHBOARD CHANGES **** //
